import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2,
  Loader2, Check, Cloud, FileText, FileSpreadsheet, Plus, Trash2,
} from "lucide-react";

const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

/* ---------------------------------------------------------------- Document (rich text) */
function DocToolbar() {
  const cmd = (c, v = null) => (e) => {
    e.preventDefault();
    document.execCommand(c, false, v);
  };
  const Btn = ({ onMouseDown, title, testid, children }) => (
    <button type="button" title={title} data-testid={testid} onMouseDown={onMouseDown}
      className="h-8 w-8 grid place-items-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900">
      {children}
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 border-b border-slate-200 px-3 py-1.5 bg-slate-50/80 sticky top-0 z-10 flex-wrap"
      data-testid="doc-toolbar">
      <Btn title="Heading 1" testid="fmt-h1" onMouseDown={cmd("formatBlock", "<h1>")}><Heading1 className="h-4 w-4" /></Btn>
      <Btn title="Heading 2" testid="fmt-h2" onMouseDown={cmd("formatBlock", "<h2>")}><Heading2 className="h-4 w-4" /></Btn>
      <Btn title="Normal text" testid="fmt-p" onMouseDown={cmd("formatBlock", "<p>")}><FileText className="h-4 w-4" /></Btn>
      <span className="w-px h-5 bg-slate-200 mx-1" />
      <Btn title="Bold" testid="fmt-bold" onMouseDown={cmd("bold")}><Bold className="h-4 w-4" /></Btn>
      <Btn title="Italic" testid="fmt-italic" onMouseDown={cmd("italic")}><Italic className="h-4 w-4" /></Btn>
      <Btn title="Underline" testid="fmt-underline" onMouseDown={cmd("underline")}><Underline className="h-4 w-4" /></Btn>
      <span className="w-px h-5 bg-slate-200 mx-1" />
      <Btn title="Bulleted list" testid="fmt-ul" onMouseDown={cmd("insertUnorderedList")}><List className="h-4 w-4" /></Btn>
      <Btn title="Numbered list" testid="fmt-ol" onMouseDown={cmd("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
    </div>
  );
}

function DocEditor({ initial, onChange }) {
  const ref = useRef(null);
  const seeded = useRef(false);
  useEffect(() => {
    if (ref.current && !seeded.current) {
      ref.current.innerHTML = initial || "<p><br></p>";
      seeded.current = true;
    }
  }, [initial]);
  return (
    <div className="flex-1 overflow-y-auto bg-slate-100">
      <DocToolbar />
      <div className="max-w-3xl mx-auto my-6 bg-white shadow-sm rounded-lg min-h-[70vh] px-12 py-10">
        <div ref={ref} contentEditable suppressContentEditableWarning data-testid="doc-editor"
          onInput={() => onChange(ref.current?.innerHTML)}
          className="one-doc prose prose-slate max-w-none outline-none text-[15px] leading-relaxed min-h-[60vh]" />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Spreadsheet (grid) */
const colName = (i) => {
  let s = "";
  i += 1;
  while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); }
  return s;
};

function SheetEditor({ initial, onChange }) {
  const [grid, setGrid] = useState(() =>
    Array.isArray(initial) && initial.length ? initial.map((r) => [...r]) : [["", "", "", "", "", ""]]);

  const push = (g) => { setGrid(g); onChange(g); };
  const setCell = (r, c, v) => { const g = grid.map((row) => [...row]); g[r][c] = v; push(g); };
  const addRow = () => push([...grid, grid[0].map(() => "")]);
  const addCol = () => push(grid.map((row) => [...row, ""]));
  const delRow = (r) => { if (grid.length <= 1) return; push(grid.filter((_, i) => i !== r)); };

  const cols = grid[0]?.length || 0;
  return (
    <div className="flex-1 overflow-auto bg-white p-4" data-testid="sheet-editor">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-9 bg-slate-100 border border-slate-200 sticky left-0 z-10" />
            {Array.from({ length: cols }).map((_, c) => (
              <th key={c} className="min-w-[120px] bg-slate-100 border border-slate-200 text-[11px] font-semibold text-slate-500 py-1">
                {colName(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, r) => (
            <tr key={r} className="group">
              <td className="w-9 bg-slate-100 border border-slate-200 text-[11px] text-slate-500 text-center relative">
                {r + 1}
                <button onClick={() => delRow(r)} title="Delete row" data-testid={`sheet-delrow-${r}`}
                  className="absolute inset-0 hidden group-hover:grid place-items-center bg-red-50 text-red-500">
                  <Trash2 className="h-3 w-3" />
                </button>
              </td>
              {row.map((val, c) => (
                <td key={c} className="border border-slate-200 p-0">
                  <input value={val} data-testid={`cell-${r}-${c}`}
                    onChange={(e) => setCell(r, c, e.target.value)}
                    className="w-full min-w-[120px] px-2 py-1 outline-none focus:bg-blue-50 focus:ring-1 focus:ring-blue-400" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={addRow} data-testid="sheet-add-row">
          <Plus className="h-3.5 w-3.5 mr-1" /> Row
        </Button>
        <Button variant="outline" size="sm" onClick={addCol} data-testid="sheet-add-col">
          <Plus className="h-3.5 w-3.5 mr-1" /> Column
        </Button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Editor shell */
export default function OneFileEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const contentRef = useRef(null);   // latest content, not for render
  const dirty = useRef(false);
  const titleRef = useRef("");

  useEffect(() => {
    let alive = true;
    api.get(`/one-files/native/${id}`)
      .then((r) => {
        if (!alive) return;
        setDoc(r.data);
        setTitle(r.data.title || "");
        titleRef.current = r.data.title || "";
        contentRef.current = r.data.content;
        setSavedAt(r.data.updated_at);
      })
      .catch(() => toast.error("Could not open this document"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const save = useCallback(async (silent = false) => {
    if (!dirty.current && silent) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/one-files/native/${id}`, {
        title: titleRef.current, content: contentRef.current,
      });
      setSavedAt(data.updated_at);
      dirty.current = false;
      if (!silent) toast.success("Saved");
    } catch { if (!silent) toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [id]);

  // autosave every 3s when dirty
  useEffect(() => {
    const t = setInterval(() => { if (dirty.current) save(true); }, 3000);
    return () => clearInterval(t);
  }, [save]);

  const onContent = (c) => { contentRef.current = c; dirty.current = true; };
  const onTitle = (v) => { setTitle(v); titleRef.current = v; dirty.current = true; };

  const goBack = async () => {
    if (dirty.current) await save(true);
    const fp = doc?.folder_path || "my-files";
    navigate(`/files?path=${encodeURIComponent(fp)}`);
  };

  if (loading) {
    return <div className="h-[calc(100vh-56px)] grid place-items-center text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>;
  }
  if (!doc) {
    return <div className="h-[calc(100vh-56px)] grid place-items-center text-slate-500">Document not found.</div>;
  }

  const isSheet = doc.native_type === "SPREADSHEET";
  return (
    <div className="flex flex-col h-[calc(100vh-56px)]" data-testid="one-file-editor">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 bg-white">
        <button onClick={goBack} data-testid="editor-back"
          className="h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:bg-slate-100">
          <ArrowLeft className="h-4 w-4" />
        </button>
        {isSheet ? <FileSpreadsheet className="h-5 w-5 text-emerald-600 shrink-0" />
          : <FileText className="h-5 w-5 text-blue-600 shrink-0" />}
        <Input value={title} onChange={(e) => onTitle(e.target.value)} data-testid="editor-title"
          className="h-8 max-w-md font-medium border-transparent hover:border-slate-200 focus:border-slate-300"
          placeholder="Untitled" />
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="editor-save-state">
            {saving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
              : savedAt ? <><Cloud className="h-3.5 w-3.5" /> Saved {fmtTime(savedAt)}</>
                : <>Unsaved</>}
          </span>
          <Button onClick={() => save(false)} data-testid="editor-save"
            className="h-8 bg-slate-900 hover:bg-slate-800 text-white">
            <Check className="h-4 w-4 mr-1" /> Save
          </Button>
        </div>
      </div>
      {isSheet
        ? <SheetEditor initial={doc.content} onChange={onContent} />
        : <DocEditor initial={doc.content} onChange={onContent} />}
    </div>
  );
}
